import { UPDATE_EVENT } from './lib/pwa';

/**
 * Registers public/sw.js, which is what makes Jarvis installable and what
 * keeps the installed app current.
 *
 * ONLY ON A PRODUCTION BUILD, and only where a service worker may run at all
 * (https, or localhost / 127.0.0.1, which the browsers treat as secure). The
 * dev server is left alone: a worker caching hot-reloaded bundles is an
 * afternoon lost to "why is my change not showing".
 *
 * HOW AN UPDATE REACHES THE PAGE. The worker calls skipWaiting() on install
 * and clients.claim() on activate, so a new deploy takes over open pages
 * within a minute of the browser noticing it - which it does on every
 * navigation, every hour while a page stays open, and whenever a tab comes
 * back to the foreground. The hand-over fires `controllerchange` here, and
 * that becomes UPDATE_EVENT on window, which lib/pwa.js turns into the
 * "Jarvis has been updated - Reload" toast. The FIRST controller a page ever
 * gets (a fresh install) is not an update and shows nothing.
 */
export function register() {
  if (process.env.NODE_ENV !== 'production') return;
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  const { protocol, hostname } = window.location;
  const local = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
  if (protocol !== 'https:' && !local) return;

  const swUrl = `${process.env.PUBLIC_URL || ''}/sw.js`;
  let hadController = Boolean(navigator.serviceWorker.controller);

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) { hadController = true; return; }
    window.dispatchEvent(new Event(UPDATE_EVENT));
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker.register(swUrl).then((reg) => {
      const check = () => reg.update().catch(() => {});
      setInterval(check, 60 * 60 * 1000);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
      });
    }).catch((err) => {
      // Not fatal: the site works exactly as before, it is just not installable.
      console.warn('Jarvis service worker did not register:', err?.message || err);
    });
  });
}

export function unregister() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  navigator.serviceWorker.ready.then((reg) => reg.unregister()).catch(() => {});
}
